import { useCallback, useEffect, useRef, useState } from 'react';
import { useNodesInitialized, useReactFlow, useStore } from '@xyflow/react';
import type { Node, NodeChange, NodeDimensionChange, NodePositionChange } from '@xyflow/react';
import { shallow } from 'zustand/shallow';
import {
  buildHelperLines,
  getHelperLines,
  snapToHelperLines,
  SpatialIndex,
  ANCHORS,
} from "@/features/canvas/engine/primitives/geometry/helper-lines";
import type { HelperLine } from "@/features/canvas/engine/primitives/geometry/helper-lines";
import { useScreenToCanvasCoords } from './use-screen-to-canvas-coords';

export interface UseHelperLinesResult {
  /** Intercept node changes to apply snapping. Wrap your onNodesChange with this. */
  applyHelperLines: (changes: NodeChange[], nodes: Node[]) => NodeChange[];
  /** Call after drag ends to rebuild the spatial index. */
  rebuildIndex: (nodes: Node[]) => void;
  horizontal: HelperLine | undefined;
  vertical: HelperLine | undefined;
}

export function useHelperLines(): UseHelperLinesResult {
  const [horizontal, setHorizontal] = useState<HelperLine | undefined>(undefined);
  const [vertical, setVertical] = useState<HelperLine | undefined>(undefined);

  const { width, height } = useStore(s => ({ width: s.width, height: s.height }), shallow);
  const { getNodes, getInternalNode } = useReactFlow();
  const screenToCanvas = useScreenToCanvasCoords();
  const nodesInitialized = useNodesInitialized();
  const indexRef = useRef(new SpatialIndex());

  const resetLines = useCallback(() => {
    setHorizontal(undefined);
    setVertical(undefined);
  }, []);

  const rebuildIndex = useCallback((nodes: Node[]) => {
    resetLines();
    const lines = buildHelperLines(nodes, getInternalNode);
    indexRef.current.initialize(lines);
  }, [getInternalNode, resetLines]);

  // Rebuild when nodes finish measuring on mount.
  useEffect(() => {
    if (nodesInitialized) rebuildIndex(getNodes());
  }, [nodesInitialized, rebuildIndex, getNodes]);

  const applyHelperLines = useCallback((changes: NodeChange[], nodes: Node[]): NodeChange[] => {
    // Only handle: single drag, single resize (bottom/right), or top/left resize (pos + dim pair).
    if (changes.length === 0 || changes.length > 2) return changes;

    resetLines();

    let posChange: NodePositionChange | undefined;
    let dimChange: NodeDimensionChange | undefined;
    let anchors = Object.keys(ANCHORS);

    if (changes.length === 1) {
      if (changes[0].type === 'position') {
        posChange = changes[0] as NodePositionChange;
      } else if (changes[0].type === 'dimensions') {
        dimChange = changes[0] as NodeDimensionChange;
        anchors = ['right', 'bottom'];
      }
    } else {
      if (changes[0].type === 'position' && changes[1].type === 'dimensions' && (changes[1] as NodeDimensionChange).dimensions) {
        posChange = changes[0] as NodePositionChange;
        dimChange = changes[1] as NodeDimensionChange;
        anchors = ['top', 'left'];
      }
    }

    const firstChange = changes[0] as { id?: string };
    const node = nodes.find(n => n.id === firstChange.id);
    if (!node) return changes;

    const internalNode = getInternalNode(node.id);
    if (!internalNode) return changes;

    const parentNode = getInternalNode(internalNode.parentId ?? '');

    // Absolute top-left of the node RIGHT NOW (pre-gesture). posChange
    // positions are parent-local, so lift them to absolute with the parent
    // origin before comparing against the (absolute) helper lines.
    const absX = internalNode.internals.positionAbsolute.x;
    const absY = internalNode.internals.positionAbsolute.y;
    const parentX = parentNode?.position.x ?? 0;
    const parentY = parentNode?.position.y ?? 0;

    // The snap search must run against the gesture's TARGET geometry, not the
    // stale current one. A posChange means the top-left is moving (a drag, or
    // a top/left resize); its target absolute top-left is posChange.position +
    // parent origin. A dimChange grows the box from whichever corner is fixed:
    // with a posChange (top/left resize) the bottom-right stays put, so it is
    // target-top-left + new dims; without one (bottom/right resize) the
    // top-left stays put, so bottom-right is current-top-left + new dims.
    const targetX = posChange?.position ? posChange.position.x + parentX : absX;
    const targetY = posChange?.position ? posChange.position.y + parentY : absY;

    const nodeBounds = {
      x: targetX,
      y: targetY,
      x2: targetX + (dimChange?.dimensions?.width ?? node.measured?.width ?? 0),
      y2: targetY + (dimChange?.dimensions?.height ?? node.measured?.height ?? 0),
    };

    const { x: vpX, y: vpY } = screenToCanvas(0, 0);
    const { x: vpX2, y: vpY2 } = screenToCanvas(width, height);
    const viewportBox = { x: vpX, y: vpY, x2: vpX2, y2: vpY2 };

    const { horizontal: hMatch, vertical: vMatch } = getHelperLines(
      indexRef.current, viewportBox, node, nodeBounds, anchors,
    );

    const snapChange: NodePositionChange = {
      id: node.id,
      type: 'position',
      position: {
        x: internalNode.internals.positionAbsolute.x,
        y: internalNode.internals.positionAbsolute.y,
      },
      dragging: false,
    };

    if (posChange?.position && snapChange.position) {
      if (parentNode) {
        snapChange.position.x = posChange.position.x + (parentNode.position.x ?? 0);
        snapChange.position.y = posChange.position.y + (parentNode.position.y ?? 0);
      } else {
        snapChange.position.x = posChange.position.x;
        snapChange.position.y = posChange.position.y;
      }
    }

    const { snappedX, snappedY } = snapToHelperLines(
      node, internalNode, snapChange, posChange, dimChange, parentNode, hMatch, vMatch,
    );

    if (snappedX || snappedY) {
      if (dimChange?.resizing || posChange?.dragging) {
        if (snappedX) setVertical(vMatch?.line);
        if (snappedY) setHorizontal(hMatch?.line);
      }
      if (dimChange && posChange) return [snapChange, dimChange];
      if (posChange) return [snapChange];
      if (dimChange) return [dimChange];
    }

    return changes;
  }, [getInternalNode, resetLines, screenToCanvas, width, height]);

  return { applyHelperLines, rebuildIndex, horizontal, vertical };
}
